import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { catchError, forkJoin, map, Observable, of } from 'rxjs'
import { BaseResponse } from '@services/api/api-base.service'
import { ManagementApiBaseService } from '@services/api/management-api-base.service'
import { emptyPaginateObservable, Paginate, ofPaginate } from '@models/paginate.model'
import {
	ElectronicProcess,
	ElectronicProcessDocument,
	ElectronicProcessDocumentPartialRequest,
	ElectronicProcessDocumentRequest,
	ElectronicProcessDocumentSearch,
	ElectronicProcessDocumentSourceType,
	ElectronicProcessPartialRequest,
	ElectronicProcessRequest,
	ElectronicProcessSearch,
	ElectronicProcessStatus
} from '@models/planning/electronic-process.model'

@Injectable({
	providedIn: 'root'
})
export class ElectronicProcessService extends ManagementApiBaseService<ElectronicProcessSearch> {
	totalElements = 0
	private readonly source: string

	constructor(override http: HttpClient) {
		super(http)
		this.source = 'electronic-processes'
		this.searchTerm = new ElectronicProcessSearch()
		this.sortColumn = 'createdAt'
	}

	paginate(query?: ElectronicProcessSearch, params?: HttpParams) {
		params = params ?? new HttpParams()
		const payload = new ElectronicProcessSearch(query ?? this.searchTerm)

		payload.object = this.normalizeSearchValue(payload.object)
		payload.externalCode = this.normalizeSearchValue(payload.externalCode)
		payload.term = this.normalizeSearchValue(payload.term)

		return this.post<ElectronicProcessSearch, Paginate<ElectronicProcess>>(`${this.source}/search`, payload, this.params(params)).pipe(
			map(response => ofPaginate(response, ElectronicProcess)),
			map(response => {
				this.totalElements = response.totalElements
				return response
			}),
			catchError(() => emptyPaginateObservable<ElectronicProcess>())
		)
	}

	/**
	 * Quantos processos há em cada status, com o mesmo termo da busca: o resumo da listagem. Uma consulta de uma linha por
	 * status, sem mexer no `totalElements` da listagem; a que falhar conta zero.
	 */
	countByStatus(statuses: ElectronicProcessStatus[], term?: string | null): Observable<Map<ElectronicProcessStatus, number>> {
		if (!statuses.length) {
			return of(new Map())
		}

		const params = new HttpParams().set('page', 0).set('size', 1)
		const requests = statuses.map(status =>
			this.post<ElectronicProcessSearch, Paginate<ElectronicProcess>>(
				`${this.source}/search`,
				new ElectronicProcessSearch({ statuses: [status], term: this.normalizeSearchValue(term) }),
				params
			).pipe(
				map(response => response?.totalElements ?? 0),
				catchError(() => of(0))
			)
		)

		return forkJoin(requests).pipe(map(totals => new Map(statuses.map((status, index) => [status, totals[index]]))))
	}

	searchDocuments(id: string, query?: ElectronicProcessDocumentSearch, params?: HttpParams) {
		params = params ?? new HttpParams()
		const payload = new ElectronicProcessDocumentSearch(query)

		return this.post<ElectronicProcessDocumentSearch, Paginate<ElectronicProcessDocument>>(
			`${this.source}/${id}/documents/search`,
			payload,
			this.params(params)
		).pipe(
			map(response => ofPaginate(response, ElectronicProcessDocument)),
			catchError(() => emptyPaginateObservable<ElectronicProcessDocument>())
		)
	}

	existsDocument(sourceId: string, sourceType: ElectronicProcessDocumentSourceType) {
		const params = new HttpParams().set('sourceId', sourceId).set('sourceType', `${sourceType}`)
		return this.get<boolean>(`${this.source}/documents/exists`, params)
	}

	save(entity: ElectronicProcessRequest) {
		return this.post<ElectronicProcessRequest, BaseResponse>(`${this.source}`, entity)
	}

	update(id: string, entity: ElectronicProcessPartialRequest) {
		return this.put<ElectronicProcessPartialRequest, BaseResponse>(`${this.source}/${id}`, entity)
	}

	detail(id: string) {
		return this.get<ElectronicProcess>(`${this.source}/${id}`).pipe(map(response => new ElectronicProcess(response)))
	}

	remove(id: string) {
		return this.delete<BaseResponse>(`${this.source}/${id}`)
	}

	cancel(id: string) {
		return this.patch(`${this.source}/${id}/cancel`)
	}

	publish(id: string) {
		return this.patch(`${this.source}/${id}/publish`)
	}

	saveDocuments(id: string, documents: ElectronicProcessDocumentRequest[]) {
		return this.post<ElectronicProcessDocumentRequest[], ElectronicProcessDocument[]>(`${this.source}/${id}/documents`, documents).pipe(
			map(response => response.map(document => new ElectronicProcessDocument(document)))
		)
	}

	updateDocument(id: string, documentId: string, document: ElectronicProcessDocumentPartialRequest) {
		return this.put<ElectronicProcessDocumentPartialRequest, BaseResponse>(`${this.source}/${id}/documents/${documentId}`, document)
	}

	removeDocument(id: string, documentId: string) {
		return this.delete<BaseResponse>(`${this.source}/${id}/documents/${documentId}`)
	}

	cancelDocument(id: string, documentId: string) {
		return this.patch(`${this.source}/${id}/documents/${documentId}/cancel`)
	}

	reprocessDocumentPagination(id: string) {
		return this.patch(`${this.source}/${id}/documents/reprocess-pagination`)
	}

	private normalizeSearchValue(value: any): string | null {
		if (!value) {
			return null
		}

		if (typeof value === 'string') {
			return value.trim() || null
		}

		if (typeof value === 'object' && 'label' in value) {
			return `${value.label ?? ''}`.trim() || null
		}

		return `${value}`.trim() || null
	}
}
